import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    tableCellClasses
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Scrollbar from '../../../../../components/Scrollbar';
import { AnalyticContext } from '../../../../../contexts/AnalyticContext';
import '../../../../../assets/styles.css';

const PERIOD_LABELS = {
    week: (dateNum, year) => `Week ${dateNum} (${year})`,
    month: (dateNum, year) => `${moment(dateNum, 'MM').format('MMMM')} (${year})`,
    quarter: (dateNum, year) => `Quarter ${dateNum} (${year})`
};

function getPeriodDate(value, year, dateType, boundary) {
    const date = moment().year(Number(year));

    if (dateType === 'week') {
        date.isoWeek(Number(value));
        return date[boundary === 'start' ? 'startOf' : 'endOf']('isoWeek');
    }
    if (dateType === 'month') {
        date.month(Number(value) - 1);
        return date[boundary === 'start' ? 'startOf' : 'endOf']('month');
    }
    if (dateType === 'quarter') {
        date.quarter(Number(value));
        return date[boundary === 'start' ? 'startOf' : 'endOf']('quarter');
    }

    return date;
}

export default function EvaluationPerPeriodTable({ customTableData, customDateType }) {
    const {
        setPerPeriodGraphData,
        setPerPeriodGraphCategory,
        perPeriodData,
        setPerPeriodData,
        setPerPeriodLoading,
        perPeriodDateType,
        setPerPeriodDateType,
        setSubModLabel
    } = React.useContext(AnalyticContext);
    const [score, setScore] = React.useState();
    const dateType = customDateType || perPeriodDateType;
    const [searchParam] = useSearchParams();
    const filterStateData = useSelector((state) => state.evalFilterFields?.evaluationFilterData);
    const navigate = useNavigate();

    React.useEffect(() => {
        if (customTableData !== undefined) {
            setScore(customTableData);
        } else {
            setScore(perPeriodData);
        }
    }, [perPeriodData, customTableData]);

    const headers = React.useMemo(() => {
        const labelForPeriod = PERIOD_LABELS[dateType];
        if (!labelForPeriod) {
            return [];
        }

        return [
            { name: 'Evaluator Name' },
            ...(score?.header || []).map((header) => ({
                name: labelForPeriod(header?.date_num, header?.year)
            })),
            { name: 'Total' }
        ];
    }, [dateType, score?.header]);

    React.useEffect(() => {
        const categories = headers.slice(1, -1).map((header) => header.name);
        setPerPeriodGraphCategory(categories);

        const graphData = (score?.score_card || []).map((evaluator, index) => ({
            name: `${evaluator?.evaluator__first_name || ''} ${evaluator?.evaluator__last_name || ''}`.trim(),
            data: (score?.header || []).map((_, periodIndex) => {
                const period = score?.data?.[index]?.[periodIndex];
                return typeof period === 'object' && period !== null ? Number(period.count) || 0 : 0;
            })
        }));
        setPerPeriodGraphData(graphData);
    }, [headers, score, setPerPeriodGraphCategory, setPerPeriodGraphData]);

    React.useEffect(() => {
        setSubModLabel(3.2);
    }, [setSubModLabel]);

    const getReportDetailsUrl = (evaluator, period, periodIndex) => {
        const filterBy = filterStateData?.filterByRange;
        const periodKey = Object.keys(period || {}).find((key) => /__(week|month|quarter)$/.test(key));
        const periodType = periodKey?.slice(periodKey.lastIndexOf('__') + 2) || dateType;
        const periodField = periodKey?.slice(0, periodKey.lastIndexOf('__')) || filterBy;
        const periodValue = periodKey ? period?.[periodKey] : undefined;
        const yearKey = `${periodField}__year`;
        const year = period?.[yearKey] || period?.year || score?.header?.[periodIndex]?.year;
        const isCustomRange = filterStateData?.dateRange === 'Custom Date Range';
        let fromDate = filterStateData?.fromDate;
        let toDate = filterStateData?.toDate;

        if (isCustomRange && periodKey && periodValue != null && year != null) {
            if (periodIndex > 0) {
                fromDate = getPeriodDate(periodValue, year, periodType, 'start').format('YYYY-MM-DD');
            }
            if (periodIndex < (score?.header?.length || 0) - 1) {
                toDate = getPeriodDate(periodValue, year, periodType, 'end').format('YYYY-MM-DD');
            }
        }

        const query = new URLSearchParams({
            from_date: moment(fromDate).format('YYYY-MM-DD'),
            to_date: moment(toDate).format('YYYY-MM-DD'),
            filterBy: filterBy || '',
            score_Card_Id: filterStateData?.scoreCard || '',
            event_type: filterStateData?.eventType || '',
            sub_type: filterStateData?.eventTypeEventSubType || '',
            group: filterStateData?.group || '',
            dateRange: filterStateData?.dateRange || '',
            teams: filterStateData?.teams || '',
            evaluator: evaluator || '',
            dateType: periodType || '',
            highlightBelow: filterStateData?.highlightBelow || searchParam.get('highlightBelow') || '',
            module: '3',
            sub_module: '2'
        });

        if (period && periodKey) {
            query.set('date_type_key', periodKey);
            query.set('date_type_value', periodValue ?? '');
            query.set('created_at_key', yearKey);
            query.set('created_at_value', year ?? '');
        }

        return `/analytics/evaluation/reportResultDetails?${query.toString()}`;
    };

    const renderCount = (period, evaluator, periodIndex) => {
        if (typeof period !== 'object' || period === null) {
            return period ?? 0;
        }

        const count = Number(period.count) || 0;
        const highlightBelow = Number(filterStateData?.highlightBelow || searchParam.get('highlightBelow'));
        const isHighlighted = highlightBelow > 0 && count < highlightBelow;

        return (
            <Button
                size="small"
                variant={isHighlighted ? 'contained' : 'text'}
                sx={{
                    backgroundColor: isHighlighted ? '#ffcb4e' : 'transparent',
                    '&:hover': { backgroundColor: isHighlighted ? '#ffcb4e' : 'transparent' }
                }}
                onClick={() => {
                    if (!customTableData) {
                        navigate(getReportDetailsUrl(evaluator, period, periodIndex));
                    }
                }}
            >
                {count}
            </Button>
        );
    };

    const rows = score?.score_card || [];
    const hasRows = rows.length > 0;
    const highlightBelow = Number(filterStateData?.highlightBelow || searchParam.get('highlightBelow'));

    return (
        <Box sx={{ mb: !customTableData ? 5 : 0 }}>
            {!customTableData && (
                <Stack sx={{ width: '100%', px: 2 }} direction="row" alignItems="center" alignContent="center" justifyContent="flex-end">
                    <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                        <ToggleButtonGroup
                            value={dateType}
                            onChange={(event) => {
                                if (!event.target.value) {
                                    return;
                                }
                                setPerPeriodDateType(event.target.value);
                                setPerPeriodLoading(true);
                                setPerPeriodData([]);
                                localStorage.setItem('dateType', event.target.value);
                            }}
                        >
                            <ToggleButton value="week" size="small">Week</ToggleButton>
                            <ToggleButton value="month" size="small">Month</ToggleButton>
                            <ToggleButton value="quarter" size="small">Year</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Stack>
            )}
            {!customTableData && <Divider />}
            <Scrollbar>
                {hasRows && (
                    <TableContainer
                        id="beyondAnalyticsTable"
                        sx={{ maxHeight: '500px', minWidth: '400px', cursor: 'default' }}
                    >
                        <Table
                            size="medium"
                            sx={{
                                [`& .${tableCellClasses.root}`]: {
                                    border: '1px solid #DEE2E6'
                                }
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    {headers.map((header, index) => (
                                        <TableCell
                                            key={`${header.name}-${index}`}
                                            sx={{
                                                px: 1,
                                                margin: '0 auto',
                                                minWidth: header.name === 'Evaluator Name' && headers.length > 7 ? '250px' : '0px'
                                            }}
                                            align={header.name === 'Evaluator Name' ? 'left' : 'center'}
                                            padding="normal"
                                        >
                                            {header.name}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((evaluator, rowIndex) => {
                                    const name = `${evaluator?.evaluator__first_name || ''} ${evaluator?.evaluator__last_name || ''}`.trim();
                                    const totalCount = Number(evaluator?.count) || 0;
                                    const totalHighlighted = highlightBelow > 0 && totalCount < highlightBelow;

                                    return (
                                        <TableRow key={evaluator?.evaluator || rowIndex}>
                                            <TableCell
                                                sx={{ px: 1, margin: '0 auto', minWidth: headers.length > 7 ? '250px' : '0px' }}
                                                align="left"
                                                padding="normal"
                                            >
                                                <Button
                                                    onClick={() => {
                                                        if (!customTableData) {
                                                            navigate(getReportDetailsUrl(evaluator?.evaluator, null, 0));
                                                        }
                                                    }}
                                                >
                                                    {name}
                                                </Button>
                                            </TableCell>
                                            {(score?.header || []).map((_, periodIndex) => (
                                                <TableCell
                                                    key={`period-${evaluator?.evaluator || rowIndex}-${periodIndex}`}
                                                    sx={{ px: 1, margin: '0 auto' }}
                                                    align="center"
                                                    padding="normal"
                                                >
                                                    {renderCount(score?.data?.[rowIndex]?.[periodIndex], evaluator?.evaluator, periodIndex)}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ px: 1, margin: '0 auto' }} align="center" padding="normal">
                                                <Button
                                                    size="small"
                                                    variant={totalHighlighted ? 'contained' : 'text'}
                                                    sx={{
                                                        backgroundColor: totalHighlighted ? '#ffcb4e' : 'transparent',
                                                        '&:hover': { backgroundColor: totalHighlighted ? '#ffcb4e' : 'transparent' }
                                                    }}
                                                    onClick={() => {
                                                        if (!customTableData) {
                                                            navigate(getReportDetailsUrl(evaluator?.evaluator, null, 0));
                                                        }
                                                    }}
                                                >
                                                    {totalCount}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Scrollbar>
        </Box>
    );
}

EvaluationPerPeriodTable.propTypes = {
    customTableData: PropTypes.object,
    customDateType: PropTypes.string
};
